import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { Observable, concat, from, of } from 'rxjs';
import { withLatestFrom, switchMap, concatMap, map, catchError } from 'rxjs/operators';

import { CheckSheetService } from '../../services/check-sheet.service';
import { State } from '@completion/reducers';
import { getCurrentProject } from '@completion/selectors';
import {
  CheckSheetActionTypes,
  CheckSheetLoadSuccess,
  GetCheckSheetMatrixTypes,
  GetCheckSheetMatrixTypesSuccess,
  UpdateCheckSheet,
  SyncCheckSheetAssignmentToMips,
  SyncCheckSheetAssignmentToMipsSuccess,
  GetCheckSheetHistory,
  GetCheckSheetHistorySuccess,
  SetOkStatusOnSignedButNotVerifiedCsas,
  LoadCsaByIds,
  LoadCsaByIdsSuccess,
  RestoreCsaItemStatuses,
  LoadCurrentCsaSuccess,
  PublishCsItemsToMips,
  PublishCsItemToMipsResult,
  PublishCsItemsToMipsDone,
} from '@completion/actions';
import { CheckSheet, CheckSheetAssignment, Project } from '@completion/models';
import { UpdateError } from '../../../commissioning-package/store/commissioning-package.action';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class CheckSheetEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly checkSheetService: CheckSheetService,
    private readonly store: Store<State>,
    private readonly toastr: NotificationService,
  ) {}

  updateCheckSheet$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateCheckSheet>(CheckSheetActionTypes.UpdateCheckSheet),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<CheckSheet[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.checkSheetService,
        method: 'updateCheckSheet',
        successAction: CheckSheetLoadSuccess,
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.checkSheet],
        // API returns CheckSheet[]; success action expects that as payload
        mapSuccessArgs: (res) => [res],
        errorAction: UpdateError,
        mapErrorArgs: (err) => [err?.error?.message ?? err?.message ?? 'Unknown error'],
      }),
    ),
  );

  getCheckSheetMatrixTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetCheckSheetMatrixTypes>(CheckSheetActionTypes.GetCheckSheetMatrixTypes),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<string[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.checkSheetService,
        method: 'getCheckSheetMatrixTypes',
        successAction: GetCheckSheetMatrixTypesSuccess,
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        mapSuccessArgs: (res) => [res],
        errorAction: UpdateError,
        mapErrorArgs: (err) => [err?.error?.message ?? err?.message ?? 'Unknown error'],
      }),
    ),
  );

  syncCheckSheetAssignmentToMips$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SyncCheckSheetAssignmentToMips>(CheckSheetActionTypes.SyncCheckSheetAssignmentToMips),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<any>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.checkSheetService,
        method: 'syncCheckSheetAssignmentToMips',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.csaId],
        onSuccess: () => {
          this.toastr.success('MIPS update succeeded for checksheet assignment');
          return new SyncCheckSheetAssignmentToMipsSuccess(0);
        },
      }),
    ),
  );

  getCheckSheetHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetCheckSheetHistory>(CheckSheetActionTypes.GetCheckSheetHistory),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<CheckSheetAssignment[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.checkSheetService,
        method: 'getCheckSheetHistory',
        successAction: GetCheckSheetHistorySuccess,
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.csaId],
        mapSuccessArgs: (res) => [res],
        errorAction: UpdateError,
        mapErrorArgs: (err) => [err?.error?.message ?? err?.message ?? 'Unknown error'],
      }),
    ),
  );

  setOkStatusOnSignedButNotVerifiedCsas$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SetOkStatusOnSignedButNotVerifiedCsas>(CheckSheetActionTypes.SetOkStatusOnSignedButNotVerifiedCsas),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<void>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.checkSheetService,
        method: 'setOkStatusOnSignedButNotVerifiedCsas',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: () => {
          this.toastr.success('OK status set on all signed but not verified check sheets');
          return { type: '[CheckSheet] Set OK Status On Signed But Not Verified Success' };
        },
      }),
    ),
  );

  loadCsaByIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadCsaByIds>(CheckSheetActionTypes.LoadCsaByIds),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<CheckSheetAssignment[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.checkSheetService,
        method: 'getCsaByIds',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.csaIds],
        onSuccess: (res) => new LoadCsaByIdsSuccess(res),
      }),
    ),
  );

  restoreItemStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RestoreCsaItemStatuses>(CheckSheetActionTypes.RestoreCsaItemStatuses),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<CheckSheetAssignment>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.checkSheetService,
        method: 'restoreStatuses',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.csaId, action.itemStatuses],
        onSuccess: (res) => {
          this.toastr.success('Item statuses restored successfully');
          return new LoadCurrentCsaSuccess(res as CheckSheetAssignment);
        },
      }),
    ),
  );

  publishCsItemsToMips$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PublishCsItemsToMips>(CheckSheetActionTypes.PublishCsItemsToMips),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      switchMap(([action, currentProject]) =>
        concat(
          from(action.itemNumbers).pipe(
            concatMap((itemNumber) =>
              this.checkSheetService.publishMipsItem(currentProject.id, action.csaId, itemNumber).pipe(
                map((result) => new PublishCsItemToMipsResult(result.itemNumber, result.published, result.error ?? null)),
                catchError((err) => of(new PublishCsItemToMipsResult(itemNumber, false, err?.message ?? 'Unknown error'))),
              ),
            ),
          ),
          of(new PublishCsItemsToMipsDone()),
        ),
      ),
    ),
  );
}
